import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GenerativeAi } from "./generativeAi/generativeAi";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import {
  Alert,
  FormLabeledInput,
  FormMultiSelectAdd,
  FormTextArea,
} from "@mikros/shared-ui";
import { Manual } from "./manual/manual";
import Spinner from "../../utils/Loader";
import {
  Chapter as C,
  Card,
  CardInfo,
  Cards,
  ChapterData,
  CourseChapter,
  CourseModuleCards,
  GenerativeAiChapters,
  GenerativeAiPost,
  CreateCourse as CC,
  ManualChapter,
  GenerativeAiRequestCards,
  Question,
} from "@mikros/library";
import ApiServices from "../../service/apiService";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 as uuidv4, v4 } from "uuid";
import S3Service from "../../service/s3Service";
import { toast } from "react-hot-toast";
import { PdfGenerativeAi } from "./pdfGenerativeAi/pdfGenerativeAi";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Loading } from "../common/loading";
import Account from "../../utils/apiConfig";
import ErrorHandler from "../../utils/errorConfig";
import AuthService from "../../service/authService";
import { jwtDecode } from "jwt-decode";
import { events } from "aws-amplify/api";

// Removed unused imports: events, Amplify, jwtDecode, ConsoleLogger

export interface CreateCourseProps {}

export function CreateCourse(props: CreateCourseProps) {
  const intl = useIntl();
  const accountInstance = Account.getInstance();
  const errorInstance = ErrorHandler.getErrorInstance();
  const siteId = "pivoinnovationlabs.sharepoint.com,26fd91a9-7849-4c7b-951b-547dfff16e3a,8a0b1259-5725-4095-9e02-be5d1d3303dd"
  const documentId="b!qZH9Jkl4e0yVG1R9__FuOlkSC4olV5VAngK-XR0zA93LoOp5kHPzQ6LNzIJsK9Rb"

  // Memoize screen labels to avoid unnecessary recalculations
  const generativeAiScreen = intl.formatMessage({
    id: "courses.model.options.generativeAi",
  });
  const generativeAiFileScreen = intl.formatMessage({
    id: "courses.model.options.generativeAiFromFile",
  });
  const manualScreen = intl.formatMessage({
    id: "courses.model.options.manual",
  });
  const courseCatalogueScreen = intl.formatMessage({
    id: "courses.model.options.fromCatalogue",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const spinner = new Spinner();

  // State hooks
  const [loading, setLoading] = useState(false);
  const [sharePointAccessToken, setSharePointAccessToken] =
    useState<string>("");

  // Manual course state, initialized with default structure
  const [manualCourse, setManualCourse] = useState<CourseModuleCards>(() => ({
    entityId: "",
    status: "",
    createdAt: new Date().toISOString(),
    createdBy: "",
    chapterCount: 0,
    date: new Date().toISOString(),
    accntId: "",
    recType: "",
    updatedAt: new Date().toISOString(),
    courseId: "",
    description: "",
    entryType: "",
    cardCount: 0,
    courseName: "",
    prompt: "",

    chapters: [
      {
        isTemprory: false,
        isLastChapter: false,
        seqNo: 0,
        entityId: "",
        status: "",
        createdAt: new Date().toISOString(),
        createdBy: "",
        chapterId: "",
        date: new Date().toISOString(),
        chapterName: "",
        recType: "",
        updatedAt: new Date().toISOString(),
        courseId: "",
        description: "",
        entryType: "",
        courseName: "",
        url: "",
        fileContent: "",
        tempUrl: "",
        newChapter: false,

        cards: [
          {
            isLastCard: false,
            seqNo: 0,
            entityId: "",
            status: "",
            createdAt: new Date().toISOString(),
            cardType: "",
            createdBy: "",
            cardId: "",
            chapterId: "",
            date: new Date().toISOString(),
            chapterName: "",
            recType: "",
            updatedAt: new Date().toISOString(),
            courseId: "",
            description: "",
            entryType: "",
            cardName: "",
            courseName: "",
            bgColor: "",
            fileName: "",
            fileSize: "",
            url: "",
            tempUrl: "",

            cardInfo: {
              cardType: "",
              cardId: "",
              card: [
                {
                  language: "",
                  text: "",
                  fileType: "",
                  url: "",
                  tempUrl: "",
                },
              ],
            },
          },
        ],

        questions: [],
      },
    ],
  }));

  // Log manualCourse.status on change
  useEffect(() => {
    console.log("Manual course status:", manualCourse?.status ?? "undefined");
  }, [manualCourse]);

  const [courseName, setCourseName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [oneChapterDelete, setOneChapterDelete] = useState<boolean>(false);
  const [isCourseCreated, setIsCourseCreated] = useState<boolean>(false);
  const [changesSaved, setChangesSaved] = useState(false);
  const [resApi, setresApi] = useState<any>({});
  const [availableChannel, setAvailableChannel] = useState<any>();
  const [certificationEnabled, setCertificationEnabled] =
    useState<boolean>(false);
  const [copy, setCopy] = useState<CourseModuleCards>();
  const [quickSaveDialog, setQuickSaveDialog] = useState<boolean>(false);
  const [submitDialog, setSubmitDialog] = useState<boolean>(false);
  const [clickSave, setClickSave] = useState(true);

  // Error and upload state
  const [errorFlag, setErrorFlag] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [uploadImageError, setUploadImageError] = useState<boolean>(false);
  const [uploadVideoError, setUploadVideoError] = useState<boolean>(false);
  const [uploadAudioError, setUploadAudioError] = useState<boolean>(false);

  // Sub state and ref
  const [sub, setSub] = useState<any>(null);
  const subRef = useRef<any>(null);

  // Derived value for Teams channel
  const hasTeams = !!availableChannel?.includes?.("teams");

  // Debug logs (grouped for clarity)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("print bool value of one chapter delete", oneChapterDelete);
      console.log("Teams channel is", hasTeams ? "available" : "not available");
      console.log("available channels print", availableChannel);
      console.log("resapi zzzzzz", resApi);
      console.log("res api", resApi);
      console.log("value of quick save dialog", quickSaveDialog);
      console.log("share point access token", sharePointAccessToken);
      console.log("quick save", clickSave);
      console.log("submit dialog", submitDialog);
      console.log("course status in create course", manualCourse.status);
    }
  }, [
    oneChapterDelete,
    hasTeams,
    availableChannel,
    resApi,
    quickSaveDialog,
    sharePointAccessToken,
    clickSave,
    submitDialog,
    manualCourse.status,
  ]);

  const toggleUploadErrorDialog = () => {
    setUploadError(!uploadError);
  };

  useEffect(() => {
    async function fetchSub() {
      let a = await AuthService.getInstance().getIdToken();
      console.log("id token course", a);
      const decoded: any = jwtDecode(a);
      const subValue = decoded["sub"];
      setSub(subValue);
      subRef.current = subValue;
      console.log("print tenant id inside use effect of edit course", subValue);
    }
    fetchSub();
  }, []);

  const apiCreateManualCourse = async (
    course: CC,
    chaptersCards: any[],
    manualCourse: CourseModuleCards
  ): Promise<boolean> => {
    // Early exit if required fields are missing
    if (!courseName || !description) return false;

    const newCC = { source: "MANUAL", ...course };
    const api = ApiServices.getInstance();

    if (!accountInstance.accntId) return false;

    // If course already created, just update manual cards
    if (isCourseCreated) {
      setCopy(manualCourse);
      return apiUpdateManualCards(course.courseId, chaptersCards);
    }

    try {
      const res = await api.makeRequest(
        `${accountInstance.accntId}/course`,
        "POST",
        newCC
      );

      if (res?.errorMessage) {
        setErrorFlag(true);
        await errorInstance.updateErrorCode(res.errorMessage);
        return false;
      }

      setIsCourseCreated(true);
      setCopy(manualCourse);
      return apiUpdateManualCards(res.courseId, chaptersCards);
    } catch (err) {
      return false;
    }
  };

  const toggleQuickSaveDialog = () => {
    setQuickSaveDialog(!quickSaveDialog);
  };
  const toggleSubmitDialog = () => {
    setSubmitDialog(!submitDialog);
  };

  const apigetPresignedUrl = async (courseId: any, filePath: string) => {
    console.log("inside create course api ");
    console.log("inside api,courseId", courseId);
    console.log("inside api,filePath ", filePath);
    let body = {
      filePath: filePath,
    };
    let api = ApiServices.getInstance();

    let apiCall = {} as any;
    if (accountInstance.accntId) {
      console.log("inside api call of presigned url");
      apiCall = await api.makeRequest(
        `${accountInstance.accntId}/course/${courseId}`,
        "POST",
        body
      );
    }

    if (apiCall) {
      console.log("api call", apiCall);
      return apiCall;
    }
  };

  const uploadToSharePoint = (e: any, accessToken: string) =>
    new Promise<object>((resolve, reject) => {
      console.log(
        "share point access token inside upload to share point",
        accessToken
      );
      console.log("inside upload to share point", e.cardInfo.card[0].url);
      const reader = new FileReader();
      const file = e.fileName;
      const fileType = file.type;

      reader.readAsArrayBuffer(file);

      reader.onload = async () => {
        try {
          const arrayBuffer = reader.result as ArrayBuffer;
          // const uploadUrl = `https://graph.microsoft.com/v1.0/sites/pivoinnovationlabs.sharepoint.com,1bc811a2-c38e-44e1-b3d9-436ce8840b9a,ddc08dfd-7332-49f2-af32-a7c6f19ca700/drives/b!ohHIG47D4USz2UNs6IQLmv2NwN0yc_JJrzKnxvGcpwBoqGARbr6lQaFFBY9mTKOh/root:/test/new test folder/test2/${file.name}:/content`;

          console.log(
            "Card info card url inside upload to share point create course",
            e.cardInfo.card[0].url
          );

          // const uploadUrl = `https://graph.microsoft.com/v1.0/sites/pivoinnovationlabs.sharepoint.com,1bc811a2-c38e-44e1-b3d9-436ce8840b9a,ddc08dfd-7332-49f2-af32-a7c6f19ca700/drives/b!ohHIG47D4USz2UNs6IQLmv2NwN0yc_JJrzKnxvGcpwBoqGARbr6lQaFFBY9mTKOh/root:/${e.cardInfo.card[0].url}:/content`;
          const uploadUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${documentId}/root:/${e.cardInfo.card[0].url}:/content`;

          const response = await fetch(uploadUrl, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": fileType,
            },
            body: arrayBuffer,
          });
          if (!response.ok) {
            const error = await response.text();
            throw new Error(`Upload failed: ${response.status} - ${error}`);
          }

          const data = await response.json();
          console.log("Upload success: ", data);
          resolve(data);
        } catch (error) {
          console.error("Share Point upload error :", error);
          reject(error);
        }
      };
    });

  const readerUpload = (e: any) =>
    new Promise<object>((resolve, reject) => {
      const s3 = S3Service.getInstance();
      const file = e.fileName;
      const cardType = e.cardType;
      const filePath = e.filePath;
      const fileType = file.type;
      const fileName = file.name;
      const courseId = particularCourseDetails.courseId;
      const cardUrl = e.cardInfo.card[0].url;

      const setError = (type: string) => {
        if (type === "VIDEO") setUploadVideoError(true);
        else if (type === "AUDIO") setUploadAudioError(true);
        else if (type === "IMG") setUploadImageError(true);
      };

      const handleUpload = async (fileObj: File) => {
        try {
          await s3.writeFileInFolder(
            filePath,
            fileObj,
            "Mikros-Cards",
            fileType
          );
          const url = (await apigetPresignedUrl(courseId, cardUrl)) as any;
          if (cardType === "IMG") {
            console.log("Success and will be resolving", url);
          }
          resolve(url);
        } catch (err) {
          if (cardType === "VIDEO") {
            console.log("Error code in video upload", err);
            setError(cardType);
          } else if (cardType === "AUDIO" || cardType === "IMG") {
            setError(cardType);
          }
          reject(err);
        }
      };

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const fileObj = s3.convertToFile(reader.result, fileType, fileName);
        if (
          cardType === "VIDEO" ||
          cardType === "AUDIO" ||
          cardType === "IMG"
        ) {
          if (cardType === "VIDEO") {
            console.log("file reader in reader upload", fileObj);
            // For video, log error after resolve for parity with original
            if (fileObj) {
              handleUpload(fileObj as File).catch((err) => {
                console.log("error in video reader upload", err);
              });
            } else {
              console.log("Invalid file object in video reader upload", e);
            }
          } else if (cardType === "IMG") {
            console.log("entered image card ");
            if (fileObj) {
              handleUpload(fileObj as File);
            } else {
              console.log("Invalid file object in image reader upload", e);
            }
          } else {
            if (fileObj) {
              handleUpload(fileObj as File);
            } else {
              console.log("Invalid file object in reader upload", e);
            }
          }
        }
      };
    });

  const getSharePointToken = async () => {
    console.log("entering the function get share point");
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      const data = await api.makeRequest(
        `${accountInstance.accntId}/course/sharepoint`,
        "GET"
      );
      if (data && data.errorMessage) {
        setErrorFlag(true);
        await errorInstance.updateErrorCode(data.errorMessage);
        return null;
      }
      setSharePointAccessToken(data); // still update state if needed elsewhere
      return data; // return the token
    }
    return null;
  };

  const handleVideoGeneration = async (e: any): Promise<any> => {
    console.log("inside handle video generation of edit course", e);

    const body = {
      courseName: e.courseName,
      chapterName: e.chapterName,
      courseId: e.courseId,
      chapterId: e.chapterId,
      cardId: e.cardId,
      accntId: accountInstance.accntId,
      createdBy: e.createdBy,
      prompt: e.prompt,
      userId: subRef.current,
      cardType: e.cardType,
    };
    console.log("body in edit course handle video generation", body);

    const accntId = body.accntId;
    const courseId = body.courseId;

    const endpoint = `${accntId}/course/${courseId}/genVideo`;

    const api = ApiServices.getInstance();

    if (accntId) {
      return await api
        .makeRequest(endpoint, "POST", body)
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            if (errorInstance && errorInstance.updateErrorCode) {
              await errorInstance.updateErrorCode(res.errorMessage);
            }
            return false;
          }
          console.log("Video generation response:", res);
          return res;
        })
        .catch(async (err) => {
          setErrorFlag(true);
          if (errorInstance && errorInstance.updateErrorCode) {
            await errorInstance.updateErrorCode("Video generation failed");
          }
          return false;
        });
    } else {
      return false;
    }
  };

  // const handleAudioGeneration = async (e: any): Promise<string | false> => {
  //   console.log("Inside handle audio generation of create course", e);

  //   const body = {
  //     courseId: e.courseId,
  //     chapterId: e.chapterId,
  //     cardId: e.cardId,
  //     accntId: accountInstance.accntId,
  //     createdBy: e.createdBy,
  //     prompt: e.prompt,
  //     cardType: e.cardType,
  //     language: e.language,
  //     languageCode: e.languageCode,
  //     style: e.style,
  //     voiceName: e.voiceName,
  //   };
  //   const accntId = body.accntId;
  //   const courseId = body.courseId;

  //   const endpoint = `${accntId}/course/${courseId}/genAudio`;
  //   const api = ApiServices.getInstance();

  //   if (!accntId) {
  //     return false;
  //   }
  //   try {
  //     const res = await api.makeRequest(endpoint, "POST", body);
  //     if (res && res.errorMessage) {
  //       setErrorFlag(true);
  //       if (errorInstance && errorInstance.updateErrorCode) {
  //         await errorInstance.updateErrorCode(res.errorMessage);
  //       }
  //       console.log("audio error response", res);
  //       return false;
  //     }
  //     console.log("Audio generation API response", res);
  //     const parsedBody = JSON.parse(res.body);
  //     const presignedUrl = parsedBody.presignedUrl;
  //     console.log("Presigned url audio being sent in create", presignedUrl);
  //     return presignedUrl;
  //   } catch (err) {
  //     console.error("Error in handle audio gen", err);
  //     setErrorFlag(true);
  //     if (errorInstance && errorInstance.updateErrorCode) {
  //       await errorInstance.updateErrorCode("Audio gen failed");
  //     }
  //     return false;
  //   }
  // };

  const handleAudioGeneration = async (
    e: any
  ): Promise<{ presignedUrl: string; prompt: string } | false> => {
    console.log("Inside handle audio generation of create course", e);
  
    const body = {
      courseId: e.courseId,
      chapterId: e.chapterId,
      cardId: e.cardId,
      accntId: accountInstance.accntId,
      createdBy: e.createdBy,
      prompt: e.prompt,
      cardType: e.cardType,
      language: e.language,
      languageCode: e.languageCode,
      style: e.style,
      voiceName: e.voiceName,
    };
    const accntId = body.accntId;
    const courseId = body.courseId;
  
    const endpoint = `${accntId}/course/${courseId}/genAudio`;
    const api = ApiServices.getInstance();
  
    if (!accntId) {
      return false;
    }
    try {
      const res = await api.makeRequest(endpoint, "POST", body);
      if (res && res.errorMessage) {
        setErrorFlag(true);
        if (errorInstance && errorInstance.updateErrorCode) {
          await errorInstance.updateErrorCode(res.errorMessage);
        }
        console.log("audio error response", res);
        return false;
      }
      console.log("Audio generation API response", res);
      const parsedBody = JSON.parse(res.body);
  
      const presignedUrl = parsedBody.presignedUrl;
      const prompt = parsedBody.prompt; // extract prompt
  
      console.log("Prompt returned in create", prompt);
      console.log("Presigned url audio being sent in create", presignedUrl);
  
  
      return { presignedUrl, prompt };
    } catch (err) {
      console.error("Error in handle audio gen", err);
      setErrorFlag(true);
      if (errorInstance && errorInstance.updateErrorCode) {
        await errorInstance.updateErrorCode("Audio gen failed");
      }
      return false;
    }
  };
  

  const handleImageGeneration = async (e: any): Promise<string | false> => {
    console.log("inside handle image generation of create course", e);

    const body = {
      courseId: e.courseId,
      chapterId: e.chapterId,
      cardId: e.cardId,
      accntId: accountInstance.accntId,
      createdBy: e.createdBy,
      prompt: e.prompt,
      cardType: e.cardType,
    };

    const accntId = body.accntId;
    const courseId = body.courseId;
    // const endpoint = `${accntId}/course/${courseId}/genMedia`;
    const endpoint = `${accntId}/course/${courseId}/genImage`;
    const api = ApiServices.getInstance();

    if (!accntId) return false;

    const channelPath = `/default/${body.cardId}`;

    try {
      console.log("Connecting to:", channelPath);
      const channel = await events.connect(channelPath);
      console.log("Connected to channel:", channel);

      const presignedUrl: string = await new Promise((resolve, reject) => {
        channel.subscribe({
          next: (data) => {
            console.log("Received data from channel subscription", data);
            try {
              // ONLY parse if it’s still a JSON string
              const eventData =
                typeof data.event === "string"
                  ? JSON.parse(data.event)
                  : data.event;

              const url = eventData?.presignedUrl;
              if (!url) throw new Error("presignedUrl missing");

              console.log("Presigned URL:", url);
              resolve(url);
            } catch (err) {
              reject("Failed to extract presignedUrl: " + err);
            }
          },
          error: (err) => {
            console.error("Error in channel subscription", err);
            // setLoading(false);
            // spinner.hideSpinner();
            reject("Channel subscription error");
          },
        });

        console.log(
          "Subscribed to channel. Now sending video generation request..."
        );

        // Fire API after subscription starts listening
        api.makeRequest(endpoint, "POST", body).then((res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            if (errorInstance && errorInstance.updateErrorCode) {
              errorInstance.updateErrorCode(res.errorMessage);
              console.log("Image error response", res);
              return res;
            }
            // spinner.hideSpinner();
            // setLoading(false);
            reject("API returned error");
          }
          console.log("Image generation API response:", res);
        });
      });
      // setLoading(false);
      // spinner.hideSpinner();
      return presignedUrl;
    } catch (err) {
      console.error("Error in handleImageGeneration:", err);
      // spinner.hideSpinner();
      // setLoading(false);
      setErrorFlag(true);
      if (errorInstance && errorInstance.updateErrorCode) {
        await errorInstance.updateErrorCode("Image generation failed");
      }
      return false;
    }

    return false;
  };

  const handleUploadGiveUrl = async (e: any) => {
    let s3Url = {};
    let sharePointUrl = {};
    console.log("inside handle upload give url", e.cardInfo.card[0].url);

    if (
      e.cardType !== "TEXT" &&
      e.cardType !== "LINK" &&
      e.cardType !== "Text"
    ) {
      console.log("Inside the type of non-text card");

      if (e?.fileName) {
        console.log("inside filename");

        try {
          s3Url = await readerUpload(e);
          console.log("Uploaded to s3:", s3Url);
        } catch (error) {
          console.error("S3 upload failed", error);
        }
        try {
          if (hasTeams) {
            console.log("inside has teams");

            const token = await getSharePointToken();
            console.log(
              "share point access token being passed to share point url",
              token
            );
            if (token) {
              sharePointUrl = await uploadToSharePoint(e, token);
            }
            console.log("uploaded to sharePoint: ", sharePointUrl);
          }
        } catch (error) {
          console.error("SharePoint upload failed", error);
        }

        return s3Url;
      }
    }
  };

  async function duplicateS3File(
    sourceKey: string,
    destinationKey: string,
    fileType: string
  ) {
    let s3 = S3Service.getInstance();
    const sourceKeyPath = sourceKey.split("/").slice(1).join("/");
    const sourceBucket = sourceKey.split("/")[0];

    const destinationKeyPath = destinationKey.split("/").slice(1).join("/");
    const destinationBucket = destinationKey.split("/")[0];
    const fileName = destinationKey.split("/").pop();
    const file = await s3.readFile(sourceKeyPath, sourceBucket);
    const fileData = await fetch(file);
    const blobData = await fileData.blob();
    const uploadablFile = new File([blobData], fileName as string);
    const resp = await s3.writeFileInFolder(
      destinationKeyPath,
      uploadablFile,
      destinationBucket,
      fileType
    );

    const url = await apigetPresignedUrl(
      particularCourseDetails.courseId,
      destinationKey
    );
    console.log("The copied url =>", url);
    return url?.url;
  }

  const apiUpdateManualCards = async (
    courseId: string,
    chaptersCards: any[]
  ): Promise<boolean> => {
    console.log("creating the chapters", chaptersCards);
    console.log("12445");
    console.log("This is the course id", courseId);
    let updateCourse = {
      courseId: courseId,
      courseName: courseName,
      description: description,
      chapter: chaptersCards,
    };

    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      return await api
        .makeRequest(
          `${accountInstance.accntId}/course/${courseId}`,
          "PUT",
          updateCourse
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return false;
          }
          console.log("Manual Course Chapters added", res);
          setChangesSaved(true);
          return true;
        })
        .catch((err) => {
          return false;
        });
    } else {
      return false;
    }
  };

  useEffect(() => {
    console.log("inside use effect create course");
    let data = manualCourse;
    const date = new Date();
    const uuid = v4();
    const cardId = v4();
    const chapterId = v4();
    data.courseId = uuid;
    data.accntId = accountInstance.accntId;
    data.date = date.toISOString();
    console.log("entered data chapters");
    data.chapters[0].chapterId = chapterId;
    data.chapters[0].courseId = uuid;
    data.chapters[0].seqNo = 1;
    data.chapters[0].entityId = v4();
    data.chapters[0].createdAt = date.toISOString();
    data.chapters[0].date = date.toISOString();
    data.chapters[0].isLastChapter = false;
    data.chapters[0].cards[0].cardId = cardId;
    data.chapters[0].cards[0].cardName = "card1";
    data.chapters[0].cards[0].cardType = "TEXT";
    data.chapters[0].cards[0].cardInfo.card[0].text = intl.formatMessage({
      id: "courses.createCourse.sampleText",
    });
    data.chapters[0].chapterName = intl.formatMessage({
      id: "courses.createCourse.chapterName",
    });
    data.chapters[0].cards[0].seqNo = 1;
    data.chapters[0].cards[0].chapterId = chapterId;
    data.chapters[0].cards[0].description = "";
    data.chapters[0].cards[0].isLastCard = false;
    data.chapters[0].cards[0].cardInfo.cardId = cardId;
    data.chapters[0].cards[0].cardInfo.cardType = "TEXT";
    data.chapters[0].cards[0].url = "undefined";

    setManualCourse(data);

    console.log("entered use effect copy");
    const copy_Object = Object.assign({}, data);
    setCopy(copy_Object);
    sessionStorage.setItem("copy", JSON.stringify(copy_Object));
    console.log("props card data", data);
    console.log("copy object", copy_Object);
  }, []);

  useEffect(() => {
    console.log("entered sample chapter create");
    let data = manualCourse;
    data.courseName = courseName;
    data.description = description;
    setManualCourse(data);
  }, [courseName, description]);

  const handleCreateManualCourse = (
    chaptersCards: ManualChapter[],
    manualCourse: CourseModuleCards
  ): Promise<boolean> => {
    const now = new Date().toISOString();
    const courseId = chaptersCards[0]?.courseId || "";
    const accntId = accountInstance.accntId;
    const course: CC = {
      accntId,
      courseName,
      createdBy: accntId,
      date: now,
      publishedBy: "",
      publishedDate: now,
      courseId,
      reviewedBy: "",
      reviewedDate: "",
      updatedBy: "",
      description,
      // tags:tagValues,
    };

    return apiCreateManualCourse(course, chaptersCards, manualCourse);
  };

  // This is only for generative ai create course form data and apis
  const [generativeAiInfo, setGenerativeAiInfo] = useState<GenerativeAiPost>(
    {} as GenerativeAiPost
  );
  const [disableFields, setDisableFields] = useState<boolean>(false);
  const [generatedChapters, setGeneratedChapters] =
    useState<GenerativeAiChapters>({
      data: { Chapters: [{} as C] } as ChapterData,
    } as GenerativeAiChapters);
  const [courseId, setCourseId] = useState<string>();

  const [particularCourseDetails, setParticularCourseDetails] =
    useState<CourseModuleCards>({
      chapters: [
        {
          cards: [{ cardInfo: { card: [{} as Card] } as CardInfo } as Cards],
        } as CourseChapter,
      ],
    } as CourseModuleCards);
  console.log("log the particular course details", courseId);

  const handleDisableFields = () => {
    setDisableFields(!disableFields);
  };

  const apiCreateGenerativeAiCourse = async (
    course: CC,
    chapterCnt: string
  ) => {
    if (!accountInstance.accntId) return;

    const newCC = { source: "AI", ...course };
    toast.custom(<Loading />, { duration: Infinity });

    const api = ApiServices.getInstance();

    try {
      const res = await api.makeRequestWithoutSpinner(
        `${accountInstance.accntId}/course`,
        "POST",
        newCC
      );

      if (res && res.errorMessage) {
        setErrorFlag(true);
        await errorInstance.updateErrorCode(res.errorMessage);
        return;
      }

      console.log("course created", res);

      setCourseId(res.courseId);
      setLoading(true);
      spinner.showSpinner();

      setGenerativeAiInfo((prev) => ({
        ...prev,
        chapterscount: chapterCnt,
        courseName: course.courseName,
        focusarea: course.description,
        temporaryId: "ID!",
      }));

      apiCallGenerativeAiChapters(
        {
          ...generativeAiInfo,
          chapterscount: chapterCnt,
          courseName: course.courseName,
          focusarea: course.description,
          temporaryId: "ID!",
        },
        res.courseId
      );
    } catch (error) {
      setErrorFlag(true);
      await errorInstance.updateErrorCode(
        "An error occurred while creating the course."
      );
    }
  };

  const [pullContentChapters, setPullContentChapters] = useState<any>();

  const fetchAiChaptersPoll = async (
    courseId: string,
    chapterCount: string,
    temporaryId: string
  ) => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequestWithoutSpinner(
          `${accountInstance.accntId}/course/${courseId}/aicourse/aichapter/pullcontent`,
          "POST",
          { chapterCount, temporaryId }
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return;
          }
          if (typeof res !== "string") {
            console.log(res);
            setPullContentChapters(res);
            spinner.hideSpinner();
            toast.remove();
          }
        });
    }
  };

  useEffect(() => {
    console.log("Polled Response", pullContentChapters);
    if (
      typeof pullContentChapters !== "string" &&
      typeof pullContentChapters !== "undefined"
    ) {
      setDisableFields(true);
      let chapters: GenerativeAiChapters = {
        status: "success",
        courseId: courseId,
        data: {
          courseName: pullContentChapters.courseName,
          Chapters: pullContentChapters.Chapters,
          TotalChapters: pullContentChapters.TotalChapters,
        },
      };

      for (let chapter of chapters.data.Chapters) {
        chapter.chapterId = v4();
      }

      setGeneratedChapters(chapters);
      setLoading(false);
      spinner.hideSpinner();
      clearTimeouts();
      sessionStorage.removeItem("courseId");
      sessionStorage.removeItem("temporaryId");
      sessionStorage.removeItem("chapterscount");
    }
  }, [pullContentChapters]);

  const [timeoutId, setTimeoutId] = useState<number[]>([]);
  const [timeOut, setChTimeOut] = useState<number>(0);
  sessionStorage.setItem("tempChTimeout", (0).toString());

  const pollForData = async (
    courseId: string,
    chapterCnt: string,
    temporaryId: string
  ): Promise<void> => {
    try {
      let response;
      if (
        courseId !== undefined &&
        chapterCnt !== undefined &&
        temporaryId !== undefined
      ) {
        response = await fetchAiChaptersPoll(courseId, chapterCnt, temporaryId);
      } else {
        const cId = sessionStorage.getItem("courseId");
        const tempId = sessionStorage.getItem("temporaryId");
        const chptCnt = sessionStorage.getItem("chapterscount");

        response = await fetchAiChaptersPoll(cId!!, tempId!!, chptCnt!!);
      }
      if (typeof pullContentChapters !== "undefined") {
        clearTimeouts();
        toast.remove();
        spinner.hideSpinner();
        setLoading(false);
        return;
      } else {
        console.log("Received unexpected response:", response);
        let t = parseInt(sessionStorage.getItem("tempChTimeout")!!);
        t = t + 5000;
        if (t < 9000000) {
          const id = setTimeout(pollForData, 5000);
          let timeout = timeoutId;
          timeout.push(id);
          setTimeoutId(timeout);
        } else {
          spinner.hideSpinner();
          toast.remove();
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error while polling:", error);
      let t = parseInt(sessionStorage.getItem("cardTimeout")!!);
      t = t + 5000;
      if (t < 9000000) {
        const id = setTimeout(pollForData, 5000);
        let timeout = timeoutId;
        timeout.push(id);
        setTimeoutId(timeout);
      } else {
        spinner.hideSpinner();
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    console.log("This is of cards");
    if (timeOut > 30000) {
      clearTimeouts();
    }
  }, [timeOut]);

  const clearTimeouts = () => {
    for (const t of timeoutId) {
      clearTimeout(t);
    }
    timeoutId.length = 0;
  };

  const apiCallGenerativeAiChapters = (
    info: GenerativeAiPost,
    courseId: string
  ) => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequestWithoutSpinner(
          `${accountInstance.accntId}/course/${courseId}/aicourse/aichapter`,
          "POST",
          info
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return;
          }
          console.log("chapter res", res);
          sessionStorage.setItem("courseId", courseId);
          sessionStorage.setItem("chapterscount", info.chapterscount);
          sessionStorage.setItem("temporaryId", info.temporaryId);
          pollForData(courseId, info.chapterscount, info.temporaryId);
        });
    }
  };

  const handleGenerateChapters = () => {
    // setGeneratedChapters({ ...datas })
  };

  // This is only for generativeAiCards CARDS
  const [aiCards, setAiCards] = useState<CourseModuleCards>({
    chapters: [
      {
        cards: [{ cardInfo: { card: [{} as Card] } as CardInfo } as Cards],
      } as CourseChapter,
    ],
  } as CourseModuleCards);

  const [aiCardState, setAiCardState] = useState<boolean>(false);

  const handleAIChapterCardChange = async (
    course: CourseModuleCards
  ): Promise<boolean> => {
    console.log("inside handle ai chapter card change", course);

    // Map chapters and clean up cards in a single pass
    const chapters: ManualChapter[] = course.chapters.map((courseChapter) => {
      // Destructure to avoid mutating original objects
      const { cards = [], ...restChapter } = courseChapter;

      // Clean up each card by omitting unwanted properties
      const cleanedCards = cards.map((card) => {
        // Use destructuring to exclude unwanted properties
        // @ts-ignore
        // eslint-disable-next-line
        const {
          entityId,
          status,
          createdAt,
          date,
          chapterName,
          entryType,
          recType,
          updatedAt,
          ...restCard
        } = card;
        return restCard;
      });

      return {
        ...restChapter,
        publishedBy: "",
        publishedDate: courseChapter.date,
        updatedBy: "",
        reviewedBy: "",
        reviewedDate: "",
        cards: cleanedCards,
        isLastChapter: courseChapter.isLastChapter,
      };
    });

    const courseBody = {
      courseName,
      description,
      courseId: course.courseId,
      chapter: chapters,
    };

    const api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      try {
        const res = await api.makeRequest(
          `${accountInstance.accntId}/course/${courseId}`,
          "PUT",
          courseBody
        );
        console.log("inside course update of handle AI card update");
        if (res && res.errorMessage) {
          setErrorFlag(true);
          await errorInstance.updateErrorCode(res.errorMessage);
          return false;
        }
        console.log("inside put of navigate");
        return true;
      } catch (err) {
        return false;
      }
    }
    return false;
  };

  const apiCallForFirstSetCards = (
    e: GenerativeAiRequestCards,
    nav: boolean
  ) => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId && !nav) {
      console.log("when nav is false");
      api
        .makeRequestWithoutSpinner(
          `${accountInstance.accntId}/course/${e.courseId}/aicourse/aicards`,
          "POST",
          e
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return;
          }
          console.log("Set Card Pull", res);
          sessionStorage.setItem("cardData", JSON.stringify(e));
          spinner.showSpinner();
          pollCardData(e);
        });
    }
    if (accountInstance.accntId && nav) {
      console.log("when nav is true");
      api
        .makeRequestWithoutSpinner(
          `${accountInstance.accntId}/course/${e.courseId}/aicourse/aicards`,
          "POST",
          e
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return;
          }
          sessionStorage.setItem("cardData", JSON.stringify(e));
        });
      navigate("/courses");
    }
  };

  const apiCallForTheCards = async (e: GenerativeAiRequestCards) => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequestWithoutSpinner(
          `${accountInstance.accntId}/course/${courseId}/aicourse/aicards/pullcontent`,
          "POST",
          e
        )
        .then(async (res) => {
          if (res && res.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(res.errorMessage);
            return;
          }
          console.log("Card Pulled Response", res);
          if (typeof res !== "string") {
            spinner.hideSpinner();
            toast.remove();
            let x = res as CourseModuleCards;
            x.chapters.sort((a, b) => a.seqNo - b.seqNo);
            x.chapters.map((ele) => {
              ele.cards.sort((a, b) => a.seqNo - b.seqNo);
            });
            let response = res as CourseModuleCards;
            response.accntId = accountInstance.accntId;
            setAiCards(response);
            setAiCardState(true);
          }
          return res;
        });
    }
  };

  useEffect(() => {
    if (aiCards.chapters.length >= 1) {
      clearCardTimeout();
      sessionStorage.removeItem("cardData");
    }
  }, [aiCardState, aiCards]);

  const [cardTimeOutIds, setCardTimeOutIds] = useState<number[]>([]);
  sessionStorage.setItem("cardTimeout", (0).toString());

  const pollCardData = async (e?: GenerativeAiRequestCards): Promise<void> => {
    try {
      // Use provided data or fallback to cached data
      const requestData =
        e ?? JSON.parse(sessionStorage.getItem("cardData") || "null");
      if (!requestData) {
        spinner.hideSpinner();
        return;
      }

      const response = await apiCallForTheCards(requestData);

      // Check if pullContentChapters is available
      if (typeof pullContentChapters === "undefined") {
        spinner.hideSpinner();
        return;
      }

      // Timeout logic
      let timeoutMs = Number(sessionStorage.getItem("cardTimeout")) || 0;
      timeoutMs += 5000;

      if (timeoutMs < 210000) {
        const id = setTimeout(() => pollCardData(), 5000);
        setCardTimeOutIds((prev) => [...prev, id as unknown as number]);
        sessionStorage.setItem("cardTimeout", timeoutMs.toString());
      } else {
        spinner.hideSpinner();
        toast.remove();
      }
    } catch (error) {
      console.error("Error while polling:", error);

      let timeoutMs = Number(sessionStorage.getItem("cardTimeout")) || 0;
      timeoutMs += 5000;

      if (timeoutMs < 210000) {
        const id = setTimeout(() => pollCardData(), 5000);
        setCardTimeOutIds((prev) => [...prev, id as unknown as number]);
        sessionStorage.setItem("cardTimeout", timeoutMs.toString());
      } else {
        spinner.hideSpinner();
      }
    }
  };

  const clearCardTimeout = () => {
    for (const t of cardTimeOutIds) {
      clearTimeout(t);
    }
    cardTimeOutIds.length = 0;
  };

  const handleGenerativeAICardRequest = (
    e: GenerativeAiRequestCards,
    nav: boolean
  ) => {
    console.log("Called this function Card request");
    spinner.showSpinner();
    toast.custom(<Loading />, { duration: Infinity });
    apiCallForFirstSetCards(e, nav);
  };

  const validator = {
    courseName: (value: string) =>
      !value
        ? intl.formatMessage({ id: "courses.createCourse.courseNameValidator" })
        : "",
    description: (value: string) =>
      !value
        ? intl.formatMessage({
            id: "courses.createCourse.courseNameDescription",
          })
        : "",
    tags: (value: string) =>
      !value
        ? intl.formatMessage({ id: "courses.createCourse.validTags" })
        : "",
  };

  const handleUpdateStatusAll = async (
    status: string,
    course?: CourseModuleCards
  ): Promise<boolean> => {
    const newCourse = course || manualCourse;
    const api = ApiServices.getInstance();

    // Clean up cards and chapters in a single pass using map
    const chapters: ManualChapter[] = newCourse.chapters.map(
      (courseChapter) => {
        // Clean up each card by omitting unwanted properties
        const cleanedCards = courseChapter.cards.map((card) => {
          // Destructure to exclude unwanted properties

          const {
            entityId,
            status,
            createdAt,
            date,
            chapterName,
            entryType,
            recType,
            updatedAt,
            ...rest
          } = card;
          return rest;
        });

        return {
          chapterName: courseChapter.chapterName,
          courseId: courseChapter.courseId,
          createdBy: courseChapter.createdBy,
          date: courseChapter.date,
          chapterId: courseChapter.chapterId,
          description: courseChapter.description,
          publishedBy: "",
          publishedDate: courseChapter.date,
          seqNo: courseChapter.seqNo,
          updatedBy: "",
          reviewedBy: "",
          reviewedDate: "",
          cards: cleanedCards,
          isLastChapter: courseChapter.isLastChapter,
          questions: courseChapter.questions,
        };
      }
    );

    const courseBody = {
      courseId: newCourse.courseId,
      chapter: chapters,
      courseName,
      description,
      status,
    };

    if (!accountInstance.accntId) return false;

    try {
      const res = await api.makeRequest(
        `${accountInstance.accntId}/course/${newCourse.courseId}/courseUpdStsAll`,
        "POST",
        courseBody
      );
      if (res && res.errorMessage) {
        setErrorFlag(true);
        await errorInstance.updateErrorCode(res.errorMessage);
        return false;
      }
      console.log("Manual Course Chapters added in update", res);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleUpdateStatusRev = async (
    status: string,
    course: CourseModuleCards
  ): Promise<boolean> => {
    // Map chapters and clean up cards in a single pass
    const chapters: ManualChapter[] = course.chapters.map((courseChapter) => {
      // Clean up each card by omitting unwanted properties
      const cleanedCards = (courseChapter.cards || []).map((card) => {
        // Destructure to exclude unwanted properties
        // @ts-ignore

        const {
          entityId,
          status,
          createdAt,
          date,
          chapterName,
          entryType,
          recType,
          updatedAt,
          ...restCard
        } = card;
        return restCard;
      });

      return {
        chapterName: courseChapter.chapterName,
        courseId: courseChapter.courseId,
        createdBy: courseChapter.createdBy,
        date: courseChapter.date,
        chapterId: courseChapter.chapterId,
        description: courseChapter.description,
        publishedBy: "",
        publishedDate: courseChapter.date,
        seqNo: courseChapter.seqNo,
        updatedBy: "",
        reviewedBy: "",
        reviewedDate: "",
        cards: cleanedCards,
        isLastChapter: courseChapter.isLastChapter,
        questions: courseChapter.questions,
      };
    });

    const courseBody = {
      courseId: course.courseId,
      chapter: chapters,
      courseName,
      description,
      status,
    };

    const api = ApiServices.getInstance();
    if (!accountInstance.accntId) return false;

    try {
      const res = await api.makeRequest(
        `${accountInstance.accntId}/course/${course.courseId}/courseUpdStsRev`,
        "POST",
        courseBody
      );
      if (res && res.errorMessage) {
        setErrorFlag(true);
        await errorInstance.updateErrorCode(res.errorMessage);
        return false;
      }
      console.log("Manual Course Chapters added in update", res);
      return true;
    } catch (e) {
      return false;
    }
  };

  // This is handleSubmit and it's common for all

  const handleSubmit = async (formData: { [name: string]: any }) => {
    let date = new Date();
    let course: CC = {
      accntId: accountInstance.accntId,
      courseName: formData["courseName"],
      courseId: v4(),
      createdBy: "40c60358-adf4-415c-bed3-f542efd4bbc6",
      date: date.toISOString(),
      publishedBy: "",
      publishedDate: date.toISOString(),
      reviewedBy: "",
      reviewedDate: "",
      updatedBy: "",
      description: formData["description"],

      // tags:formData["tags"],
    };
    if (location.state === generativeAiScreen) {
      if (courseId === undefined) {
        apiCreateGenerativeAiCourse(course, formData["chapterCnt"]);
      }
    }
  };

  useEffect(() => {
    async function initializeAndUseAccount() {
      await accountInstance.fetchAccountId();
      console.log(
        "account id instance in tenant side of create course",
        accountInstance.accntId
      );
      console.log(
        "account instance in account role",
        accountInstance.accntRole
      );
    }
    initializeAndUseAccount();
  }, []);

  useEffect(() => {
    let api = ApiServices.getInstance();
    if (accountInstance.accntId) {
      api
        .makeRequest(`${accountInstance.accntId}/organization/channels`, "GET")
        .then(async (data) => {
          if (data && data.errorMessage) {
            setErrorFlag(true);
            await errorInstance.updateErrorCode(data.errorMessage);
            return;
          }
          console.log("Data received from organization", data);

          const enabledChannels = Object.keys(data[0].distbAcc).filter(
            (channel) => data[0].distbAcc[channel].enabled
          );
          console.log(
            "certification enabled value in use effect",
            data[0]?.featureAccess?.certification
          );
          setAvailableChannel(enabledChannels);
          // setCertificationEnabled(data[0]?.featureAccess?.certification);
        });
    }
  }, []);

  return (
    <div className="h-full w-full flex flex-col xl:pb-[7%] pb-[12%]">
      {/* Error Dialog */}
      {(errorFlag ||
        uploadError ||
        (quickSaveDialog && clickSave) ||
        submitDialog) && (
        <>
          {errorFlag && (
            <Dialog className="errorDialogue">
              <Alert
                description={intl.formatMessage({
                  id: errorInstance.errorCode,
                })}
                buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
                icon="errorShape.png"
                iconMsg={intl.formatMessage({ id: "iconMsgError" })}
                backgroundColor="mk-red"
                onClick={() => {
                  setErrorFlag(false);
                  if (errorInstance.permDenied === true) {
                    navigate("/home");
                  }
                }}
              />
            </Dialog>
          )}
          {uploadError && (
            <Dialog className="errorDialogue">
              <Alert
                description={intl.formatMessage({
                  id: uploadImageError
                    ? "course.createCourse.uploadImageError"
                    : uploadVideoError
                    ? "courses.createCourse.uploadVideoError"
                    : uploadAudioError
                    ? "courses.createCourse.uploadAudioError"
                    : "",
                })}
                buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
                icon="errorShape.png"
                iconMsg={intl.formatMessage({ id: "iconMsgError" })}
                backgroundColor="mk-red"
                onClick={toggleUploadErrorDialog}
              />
            </Dialog>
          )}
          {quickSaveDialog && clickSave && (
            <Dialog className="confirmSuccessDialogue">
              <Alert
                description={intl.formatMessage({
                  id: "course.saveChangesSuccess",
                })}
                buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
                icon="successShape.png"
                iconMsg={intl.formatMessage({ id: "iconMsgSuccess" })}
                backgroundColor="mk-bright-green"
                onClick={toggleQuickSaveDialog}
              />
            </Dialog>
          )}
          {submitDialog && (
            <Dialog className="confirmSuccessDialogue">
              <Alert
                description={intl.formatMessage({
                  id: "course.submitCourseSuccess",
                })}
                buttonText={intl.formatMessage({ id: "users.buttonText.Ok" })}
                icon="successShape.png"
                iconMsg={intl.formatMessage({ id: "iconMsgSuccess" })}
                backgroundColor="mk-bright-green"
                onClick={() => {
                  toggleSubmitDialog();
                  navigate("/courses");
                }}
              />
            </Dialog>
          )}
        </>
      )}

      <Form
        onSubmit={handleSubmit}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement className="flex flex-col space-y-4">
            {/* Course Name, Description, Tags */}
            {location.state !== generativeAiFileScreen && (
              <>
                <div>
                  <Field
                    name="courseName"
                    id="courseName"
                    label={
                      <strong>
                        <FormattedMessage id="courses.onClickCourse.courseName" />
                      </strong>
                    }
                    component={FormLabeledInput}
                    disabled={disableFields}
                    validator={validator.courseName}
                    onChange={(e) => setCourseName(e.value)}
                    className="bg-white w-[25%]"
                  />
                </div>
                <div>
                  <Field
                    name="description"
                    id="description"
                    label={
                      <strong>
                        {disableFields
                          ? intl.formatMessage({
                              id: "courses.onClickCourse.description",
                            })
                          : intl.formatMessage({
                              id: "courses.generativeAi.describe",
                            })}
                      </strong>
                    }
                    component={FormTextArea}
                    disabled={disableFields}
                    validator={validator.description}
                    onChange={(e) => setDescription(e.value)}
                    className="bg-white w-2/3 "
                  />
                </div>
                <div className={disableFields ? "hidden" : "w-[60%]"}>
                  <Field
                    name="tags"
                    id="tags"
                    label={
                      <strong>
                        <FormattedMessage id="courses.createCourse.tags" />
                      </strong>
                    }
                    component={FormMultiSelectAdd}
                    className="bg-white"
                    data={[
                      intl.formatMessage({ id: "courses.pdfGeneratvieAi.AI" }),
                      intl.formatMessage({
                        id: "courses.pdfGeneratvieAi.Testing",
                      }),
                      intl.formatMessage({
                        id: "courses.pdfGeneratvieAi.Deployment",
                      }),
                      intl.formatMessage({
                        id: "courses.pdfGeneratvieAi.Tech",
                      }),
                      intl.formatMessage({
                        id: "courses.pdfGeneratvieAi.Android",
                      }),
                    ]}
                  />
                </div>
              </>
            )}

            {/* Main Content Switch */}
            <div>
              {(() => {
                switch (location.state) {
                  case manualScreen:
                    return (
                      <Manual
                        handleUploadGiveUrl={handleUploadGiveUrl}
                        handleVideoGeneration={handleVideoGeneration}
                        handleImageGeneration={handleImageGeneration}
                        handleAudioGeneration={handleAudioGeneration}
                        duplicateS3File={duplicateS3File}
                        handleCreateCourseUpdateChapterCards={
                          handleCreateManualCourse
                        }
                        manualCourse={manualCourse}
                        oneChapterDeleteFunction={setOneChapterDelete}
                        tenantRole={accountInstance.accntRole}
                        handleUpdateStatusRev={handleUpdateStatusRev}
                        handleUpdateStatusAll={handleUpdateStatusAll}
                      />
                    );
                  case courseCatalogueScreen:
                    return <div />;
                  case generativeAiFileScreen:
                    return (
                      <PdfGenerativeAi
                        duplicateS3File={duplicateS3File}
                        handleUpdateStatusAll={handleUpdateStatusAll}
                        handleUpdateStatusRev={handleUpdateStatusRev}
                        handleUploadGiveUrl={handleUploadGiveUrl}
                      />
                    );
                  default:
                    return (
                      <GenerativeAi
                        duplicateS3File={duplicateS3File}
                        handleUpdateStatusAll={handleUpdateStatusAll}
                        handleUpdateStatusRev={handleUpdateStatusRev}
                        handleUploadGiveUrl={handleUploadGiveUrl}
                        changeToCardState={aiCardState}
                        handleRequestCard={(
                          e: GenerativeAiRequestCards,
                          nav?: boolean
                        ) => handleGenerativeAICardRequest(e, !!nav)}
                        handleAIChapterCardChange={handleAIChapterCardChange}
                        cards={aiCards!}
                        chapters={generatedChapters}
                        handleState={handleDisableFields}
                        changeState={disableFields}
                        formRenderProps={formRenderProps}
                      />
                    );
                }
              })()}
            </div>

            {/* Action Buttons */}
            {!disableFields && (
              <div
                className={
                  location.state === manualScreen ||
                  location.state === generativeAiFileScreen
                    ? "hidden"
                    : "w-full flex justify-end items-center"
                }
              >
                <Button
                  themeColor="primary"
                  onClick={() => navigate("/courses")}
                  className="mr-12"
                >
                  <FormattedMessage id="backButton" />
                </Button>
                <Button themeColor="primary" onClick={handleGenerateChapters}>
                  <FormattedMessage id="generateChaptersButton" />
                </Button>
              </div>
            )}
          </FormElement>
        )}
      />
    </div>
  );
}
